import { type ComponentProps } from 'react';

// eslint-disable-next-line no-restricted-imports -- This adapter owns the Atlaskit fallback; app code must use ChoreographedSpotlight.
import { Spotlight as AtlaskitSpotlight } from '@atlaskit/onboarding';
import {
  choreographerFactory,
  ProductIds,
  type ChoreographerFactory,
} from '@atlassian/ipm-choreographer';
import type { envType } from '@atlassiansox/analytics-web-client';

import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';
import { IS_SERVER } from 'src/utils/ssr';

export const transformBbEnvToChoreographerEnv = (env: BbEnv): envType => {
  if (env === BbEnv.Production) {
    return 'prod' as envType;
  }

  if (env === BbEnv.Staging || env === BbEnv.Integration) {
    return 'staging' as envType;
  }

  return 'dev' as envType;
};

let bitbucketChoreographer: ChoreographerFactory | null = null;

const getBitbucketChoreographer = (): ChoreographerFactory => {
  if (!bitbucketChoreographer) {
    bitbucketChoreographer = choreographerFactory(
      ProductIds.BITBUCKET,
      transformBbEnvToChoreographerEnv(getBbEnv()),
      { sourceProductId: ProductIds.BITBUCKET },
      { enableChoreographer: true }
    );
  }

  return bitbucketChoreographer;
};

type ChoreographedSpotlightProps = ComponentProps<typeof AtlaskitSpotlight> & {
  readonly messageId: string;
};

export const ChoreographedSpotlight = ({
  messageId,
  ...spotlightProps
}: ChoreographedSpotlightProps) => {
  const isChoreographerEnabled = useStatsigGate(
    StatsigFeatureKeys.ipmChoreographer
  );

  if (!isChoreographerEnabled || IS_SERVER) {
    return <AtlaskitSpotlight {...spotlightProps} />;
  }

  const { Spotlight } = getBitbucketChoreographer();

  return (
    <Spotlight
      {...spotlightProps}
      messageId={messageId}
      messageType="engagement"
    />
  );
};
